import { createContext, ReactNode, useReducer, useState } from 'react';
import { UserTraitsResponse } from '@myme/api';
import { handleLogout } from '@myme/logic/src/kratos';
import { kratosIntegration, retrieveKratosErrors } from '@myme/utils';
import { useTranslation } from 'src/hooks';
import { feedPageRoute } from 'src/routes/route-list';
import { toast, ToastType } from 'src/utils';
import { mapKratosResponseToAuthUser, mapToAuthUser } from './mapToAuthUser';
import { AuthAction, AuthContextValue, AuthState, UserData } from './types';
import { useInitializeAuth } from './useInitializeAuth';

const initialAuthState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null
};

export const AuthContext = createContext<AuthContextValue>({
  ...initialAuthState,
  requestedLocation: null,
  login: () => {},
  logout: async () => {},
  localLogout: () => {},
  setRequestedPath: () => {},
  updateUserData: () => {}
});

const reducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'INITIALIZE': {
      const { isAuthenticated, user } = action.payload;

      return {
        ...state,
        isInitialized: true,
        isAuthenticated,
        user
      };
    }
    case 'LOGIN': {
      const { user } = action.payload;
      return {
        ...state,
        isAuthenticated: true,
        user
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        user: null
      };
    }
    case 'UPDATE_USER_DATA': {
      const { user } = action.payload;
      return {
        ...state,
        user: {
          role: state.user?.role || 'USER',
          ...user
        }
      };
    }
    default: {
      return { ...state };
    }
  }
};
interface AuthProviderProps {
  children: ReactNode;
  overrideInitialState?: AuthState; // helpful for testing purposes
}

export const AuthProvider = ({ children, overrideInitialState }: AuthProviderProps) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, overrideInitialState || initialAuthState);
  const [requestedLocation, setRequestedLocation] = useState<string | null>(null);

  const login = (user: UserData) => {
    dispatch({
      type: 'LOGIN',
      payload: {
        user: mapToAuthUser(user)
      }
    });
  };

  const logout = async () => {
    try {
      const logoutFlow = await kratosIntegration.request.createSelfServiceLogoutFlowUrlForBrowsers();
      await handleLogout(logoutFlow.data.logout_token);
      setRequestedLocation(feedPageRoute);
      dispatch({ type: 'LOGOUT' });
    } catch (e: unknown) {
      const errors = retrieveKratosErrors(e);
      errors.forEach(error => toast(ToastType.ERROR, t(`errors.kratos.${error}`)));
    }
  };

  const localLogout = () => {
    dispatch({ type: 'LOGOUT' });
  };

  const updateUserData = (user: UserTraitsResponse) => {
    dispatch({
      type: 'UPDATE_USER_DATA',
      payload: {
        user: mapKratosResponseToAuthUser(user)
      }
    });
  };

  const setRequestedPath = (path: string | null) => {
    setRequestedLocation(path);
  };

  useInitializeAuth(dispatch, overrideInitialState);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        requestedLocation,
        login,
        logout,
        localLogout,
        setRequestedPath,
        updateUserData
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
